// Shared ApexCharts config + helpers for the warm Sartor analytics views.

export const ACCENT = "#b52118";
export const INK = "#1a1814";
export const SOFT = "#756f64";
export const FAINT = "#a39a8c";
export const LINE = "#ece7de";

export const SOURCE_LABELS = {
  direct: "Directo",
  landing: "Inicio",
  search: "Búsqueda",
  recommendation: "Recomendación"
};

// brand-aligned colors per source (stable order)
export const SOURCE_COLORS = {
  search: ACCENT,
  landing: INK,
  recommendation: "#c89b3c",
  direct: SOFT
};

// The browser's IANA timezone (e.g. "America/Argentina/Buenos_Aires"). The
// analytics backend buckets visits/inquiries by day in this zone so the charts
// match the client's local calendar instead of UTC.
export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// YYYY-MM-DD for a Date using its LOCAL calendar day. (toISOString() would use
// UTC and shift the day for negative-offset zones like GMT-3.)
export function localDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Fill a sparse [{ label: 'YYYY-MM-DD', count }] series with zeros across the
// last `days` (ending today) so the time-series line is continuous. Keys are
// built in local time to line up with the backend's timezone-aware labels.
export function fillDaily(buckets, days) {
  const map = {};
  (buckets || []).forEach(b => {
    map[b.label] = b.count;
  });
  const out = [];
  const end = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    const key = localDateKey(d);
    out.push({ x: key, y: map[key] || 0 });
  }
  return out;
}

// area chart options (time-series)
export function areaOptions(color = ACCENT) {
  return {
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "Roboto, sans-serif"
    },
    colors: [color],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 0.2, opacityFrom: 0.35, opacityTo: 0.02 }
    },
    grid: { borderColor: LINE, strokeDashArray: 4, padding: { left: 4, right: 4 } },
    xaxis: {
      type: "datetime",
      labels: { style: { colors: FAINT, fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: FAINT }, formatter: v => Math.round(v) }
    },
    tooltip: { x: { format: "dd MMM" } }
  };
}

// area chart for two series (e.g. Visitas vs Búsquedas) — same look as
// areaOptions but with a legend and a second color.
export function dualAreaOptions(colors = [ACCENT, "#c89b3c"]) {
  return {
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "Roboto, sans-serif"
    },
    colors,
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: { shadeIntensity: 0.2, opacityFrom: 0.28, opacityTo: 0.02 }
    },
    grid: { borderColor: LINE, strokeDashArray: 4, padding: { left: 4, right: 4 } },
    legend: {
      position: "top",
      horizontalAlign: "right",
      labels: { colors: SOFT },
      fontSize: "13px",
      markers: { radius: 12 }
    },
    xaxis: {
      type: "datetime",
      labels: { style: { colors: FAINT, fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: FAINT }, formatter: v => Math.round(v) }
    },
    tooltip: { x: { format: "dd MMM" } }
  };
}

// The Reportes hero chart: this period as a filled area, the one before it as a
// dotted line on the same axis. The comparison series carries its own x values
// (the previous period's dates), so it's plotted against an index rather than a
// datetime axis — the category labels come from the current period.
export function comparisonOptions(labels, color = ACCENT) {
  return {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "Roboto, sans-serif",
      animations: { enabled: false }
    },
    colors: [color, "#cdc4b5"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: [2, 1.5], dashArray: [0, 5] },
    // No per-series `opacity` here: a line-type series takes its stroke colour from
    // fill.opacity, so [1, 0] renders the comparison line fully transparent. The
    // second series is type "line", which never draws an area anyway.
    fill: {
      type: ["gradient", "solid"],
      gradient: { shadeIntensity: 0.2, opacityFrom: 0.32, opacityTo: 0.02 }
    },
    grid: { borderColor: LINE, strokeDashArray: 4, padding: { left: 4, right: 6, top: 0 } },
    legend: { show: false },
    xaxis: {
      type: "category",
      categories: labels,
      tickAmount: 6,
      labels: { style: { colors: FAINT, fontSize: "10.5px" }, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: FAINT, fontSize: "10.5px" }, formatter: v => Math.round(v) }
    },
    tooltip: { shared: true, intersect: false }
  };
}

// donut options for a source breakdown; pass the ordered source keys
export function donutOptions(sourceKeys) {
  return {
    chart: { type: "donut", fontFamily: "Roboto, sans-serif" },
    labels: sourceKeys.map(k => SOURCE_LABELS[k] || k),
    colors: sourceKeys.map(k => SOURCE_COLORS[k] || SOFT),
    legend: { position: "bottom", labels: { colors: SOFT }, fontSize: "13px" },
    dataLabels: { enabled: true, formatter: v => Math.round(v) + "%" },
    stroke: { width: 0 },
    plotOptions: { pie: { donut: { size: "62%" } } },
    tooltip: { y: { formatter: v => `${v} visita${v === 1 ? "" : "s"}` } }
  };
}
